// src/views/reports/ProfitLossReportView.js

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase/config';
import { collection, query, getDocs } from 'firebase/firestore';
import { Link, Navigate } from 'react-router-dom';
import { 
    ArrowLeft, TrendingUp, TrendingDown, DollarSign, 
    Calendar, Download, BarChart2 
} from 'lucide-react';
import { 
    startOfYear, endOfYear, format, parseISO, 
    eachMonthOfInterval, isSameMonth 
} from 'date-fns';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, 
    Tooltip as RechartsTooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { formatCurrency } from '../../utils/formatters';
import { exportToCSV } from '../../utils/csvExport';
import { grossInvoiceTotal, creditNotesInPeriod } from '../../utils/invoiceNetRevenue'; // netrev-6f2a8c53 (R1d-5) - revenue net of credit notes; cnperiod-5a3e7d92 - each in the month of its own date

const ProfitLossReportView = ({ currentUserProfile }) => {
    const [invoices, setInvoices] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);

    // Default: This Year
    const [startDate, setStartDate] = useState(format(startOfYear(new Date()), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(endOfYear(new Date()), 'yyyy-MM-dd'));

    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;

        const fetchData = async () => {
            try {
                // 1. Fetch Invoices
                const invSnap = await getDocs(query(collection(db, `organizations/${orgId}/invoices`)));
                const invData = invSnap.docs.map(doc => ({ 
                    id: doc.id, 
                    ...doc.data()
                }));

                // 2. Fetch Expenses
                const expSnap = await getDocs(query(collection(db, `organizations/${orgId}/expenses`)));
                const expData = expSnap.docs.map(doc => ({ 
                    id: doc.id, 
                    ...doc.data()
                }));

                setInvoices(invData);
                setExpenses(expData);
                setLoading(false);
            } catch (error) {
                console.error("Error loading P&L data:", error);
                setLoading(false);
            }
        };

        fetchData();
    }, [currentUserProfile]);

    // --- METRICS ENGINE (Strictly Aligned with Dashboard) ---
    const { chartData, totals } = useMemo(() => {
        const start = parseISO(startDate);
        const end = parseISO(endDate);
        end.setHours(23, 59, 59, 999); // Full day inclusion

        // 1. Robust Date Parsers (Matches ReportsHome.js)
        const getInvoiceDate = (inv) => {
            if (inv.createdAt?.toDate) return inv.createdAt.toDate();
            if (inv.issuedAt?.toDate) return inv.issuedAt.toDate();
            if (inv.date) return new Date(inv.date);
            return new Date(); 
        };

        const getExpenseDate = (exp) => {
            if (exp.date?.toDate) return exp.date.toDate();
            return new Date(exp.date || new Date());
        };

        // 2. Strict Filtering (Matches ReportsHome.js)
        const filteredInvoices = invoices.filter(inv => {
            // CRITICAL FIX: Exclude 'Cancelled' to match Dashboard
            if (['Draft', 'Void', 'Cancelled'].includes(inv.status)) return false;
            
            const date = getInvoiceDate(inv);
            inv.reportDate = date; // Store for bucketing
            return date >= start && date <= end;
        });

        const filteredExpenses = expenses.filter(exp => {
            const date = getExpenseDate(exp);
            exp.reportDate = date; // Store for bucketing
            return date >= start && date <= end;
        });

        // cnperiod-5a3e7d92 -- credit notes ISSUED in these dates (whatever month their invoice was
        // billed in); each comes off the month of its own date below. [] when there are none.
        const periodCredits = creditNotesInPeriod(invoices.filter(inv => !['Draft', 'Void', 'Cancelled'].includes(inv.status)), start, end, getInvoiceDate);

        // 3. Generate Month Buckets
        const months = eachMonthOfInterval({ start, end });
        
        let totalRev = 0;
        let totalExp = 0;

        const data = months.map(month => {
            const monthLabel = format(month, 'MMM yyyy');
            
            // Sum Revenue
            const monthRevenue = filteredInvoices
                .filter(i => isSameMonth(i.reportDate, month))
                .reduce((sum, i) => sum + grossInvoiceTotal(i), 0) // parseFloat(i.total) || 0, as ever
                - periodCredits.filter(c => isSameMonth(c.date, month)).reduce((sum, c) => sum + c.amount, 0); // cnperiod-5a3e7d92 -- less the credit notes issued THIS month

            // Sum Expenses
            const monthExpenses = filteredExpenses
                .filter(e => isSameMonth(e.reportDate, month))
                .reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);

            totalRev += monthRevenue;
            totalExp += monthExpenses;

            return {
                name: monthLabel,
                Revenue: monthRevenue,
                Expenses: monthExpenses,
                Net: monthRevenue - monthExpenses
            };
        });

        return { 
            chartData: data, 
            totals: { 
                revenue: totalRev, 
                expenses: totalExp, 
                net: totalRev - totalExp,
                margin: totalRev > 0 ? ((totalRev - totalExp) / totalRev) * 100 : 0
            } 
        };
    }, [invoices, expenses, startDate, endDate]);

    const handleExport = () => {
        const csvData = chartData.map(row => ({
            Month: row.name,
            Revenue: row.Revenue.toFixed(2),
            Expenses: row.Expenses.toFixed(2),
            Net_Profit: row.Net.toFixed(2)
        }));
        exportToCSV(csvData, `Profit_Loss_${startDate}_to_${endDate}.csv`);
    };

    // --- FIX: REMOVED HARDCODED PERMISSION CHECK ---

    if (loading) return <div className="p-10 text-center text-gray-500">Calculating Profit & Loss...</div>;

    return (
        <div className="p-6 md:p-10 bg-gray-50 min-h-screen">
            {/* HEADER */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
                <div className="flex items-center gap-4">
                    <Link to="/reports" className="p-2 bg-white border rounded-full hover:bg-gray-100 transition-colors">
                        <ArrowLeft size={20} className="text-gray-600" />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Profit & Loss</h1>
                        <p className="text-gray-500 text-sm">Income vs Expenses over time</p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2 bg-white p-2 rounded-md border shadow-sm">
                        <Calendar size={18} className="text-gray-400" />
                        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="text-sm border-none focus:ring-0 text-gray-600" />
                        <span className="text-gray-400">-</span>
                        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="text-sm border-none focus:ring-0 text-gray-600" />
                    </div>
                    <button onClick={handleExport} className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors shadow-sm">
                        <Download size={18} /> Export
                    </button>
                </div>
            </div>

            {/* TOTALS ROW */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white p-6 rounded-lg border shadow-sm border-l-4 border-l-green-500">
                    <p className="text-sm text-gray-500 mb-1">Total Revenue</p>
                    <h3 className="text-3xl font-bold text-gray-900">{formatCurrency(totals.revenue)}</h3>
                </div>
                <div className="bg-white p-6 rounded-lg border shadow-sm border-l-4 border-l-red-500">
                    <p className="text-sm text-gray-500 mb-1">Total Expenses</p>
                    <h3 className="text-3xl font-bold text-gray-900">{formatCurrency(totals.expenses)}</h3>
                </div>
                <div className={`bg-white p-6 rounded-lg border shadow-sm border-l-4 ${totals.net >= 0 ? 'border-l-blue-600' : 'border-l-orange-500'}`}>
                    <p className="text-sm text-gray-500 mb-1">Net Profit ({totals.margin.toFixed(1)}% Margin)</p>
                    <div className="flex items-center gap-2">
                        <h3 className={`text-3xl font-bold ${totals.net >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                            {formatCurrency(totals.net)}
                        </h3>
                        {totals.net >= 0 ? <TrendingUp className="text-blue-600" /> : <TrendingDown className="text-orange-600" />}
                    </div>
                </div>
            </div>

            {/* CHART */}
            <div className="bg-white p-6 rounded-lg border shadow-sm mb-8">
                <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
                    <BarChart2 size={20} className="text-blue-600" /> Monthly Breakdown
                </h3>
                <div className="h-80 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="name" fontSize={12} tickLine={false} axisLine={false} />
                            <YAxis fontSize={12} tickLine={false} axisLine={false} tickFormatter={(val) => `$${val/1000}k`}/>
                            <RechartsTooltip formatter={(val) => formatCurrency(val)} cursor={{fill: 'transparent'}} />
                            <Legend />
                            <Bar dataKey="Revenue" fill="#22c55e" radius={[4, 4, 0, 0]} barSize={30} />
                            <Bar dataKey="Expenses" fill="#ef4444" radius={[4, 4, 0, 0]} barSize={30} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* TABLE */}
            <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
                <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50 border-b">
                        <tr>
                            <th className="px-6 py-4 font-semibold text-gray-600">Month</th>
                            <th className="px-6 py-4 font-semibold text-gray-600 text-right">Revenue</th>
                            <th className="px-6 py-4 font-semibold text-gray-600 text-right">Expenses</th>
                            <th className="px-6 py-4 font-semibold text-gray-600 text-right">Net Profit</th>
                            <th className="px-6 py-4 font-semibold text-gray-600 text-right">Margin</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {chartData.map((row) => (
                            <tr key={row.name} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 font-medium text-gray-900">{row.name}</td>
                                <td className="px-6 py-4 text-right text-green-600 font-medium">{formatCurrency(row.Revenue)}</td>
                                <td className="px-6 py-4 text-right text-red-600 font-medium">{formatCurrency(row.Expenses)}</td>
                                <td className={`px-6 py-4 text-right font-bold ${row.Net >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                                    {formatCurrency(row.Net)}
                                </td>
                                <td className="px-6 py-4 text-right text-gray-500">
                                    {row.Revenue > 0 ? ((row.Net / row.Revenue) * 100).toFixed(1) + '%' : '0.0%'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ProfitLossReportView;